import { useState } from "react";
import { useConvex, usePaginatedQuery, useQuery } from "convex/react";
import { toMassMg } from "@anthemreactv2/contracts";
import { api } from "@anthemreactv2/backend/labs-api";
import type {
  Doc,
  Id,
} from "@anthemreactv2/backend/convex/_generated/dataModel";
import { CloudGate } from "./cloud";
import {
  SharedAction,
  fieldNumber as number,
  fieldText as text,
  type ActionField,
} from "./SharedAction";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeading, Panel, SelectField } from "./components/workbench";
import { SharedRecordings } from "./SharedRecordings";
import type { Workspace } from "./workspace";

const kg = (data: FormData, name: string) => toMassMg(number(data, name), "kg");
const fmt = (mg: number) =>
  `${(mg / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 3 })} kg`;
const reason: ActionField = { name: "reason", label: "Reason / reference" };
const weight: ActionField = {
  name: "weight",
  label: "Net weight (kg)",
  type: "number",
  min: 0.000001,
};
function More({
  status,
  loadMore,
}: {
  status: string;
  loadMore: (count: number) => void;
}) {
  return status === "CanLoadMore" || status === "LoadingMore" ? (
    <Button
      variant="ghost"
      disabled={status === "LoadingMore"}
      onClick={() => loadMore(25)}
    >
      {status === "LoadingMore" ? "Loading…" : "Show more"}
    </Button>
  ) : null;
}
function Empty({ children }: { children: string }) {
  return (
    <p className="rounded-xl border border-dashed p-6 text-sm text-muted-foreground">
      {children}
    </p>
  );
}

export function SharedRoastery({
  workspace,
  onOpen,
}: {
  workspace: Workspace | null;
  onOpen: (workspace: Workspace) => void;
}) {
  return (
    <>
      <PageHeading
        title="Roastery operations"
        description="Green coffee in. Roasts measured. Quality signed off."
      />
      <CloudGate>
        <SharedHome workspace={workspace} onOpen={onOpen} />
      </CloudGate>
    </>
  );
}
function SharedHome({
  workspace,
  onOpen,
}: {
  workspace: Workspace | null;
  onOpen: (workspace: Workspace) => void;
}) {
  const mine = useQuery(api.labs.roasteries.mine, {}),
    client = useConvex();
  const [selected, setSelected] = useState("");
  const roastery =
    mine?.roasteries.find((row) => row._id === selected) ?? mine?.roasteries[0];
  if (!mine) return <p role="status">Loading your roasteries…</p>;
  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        {mine.roasteries.length > 0 && (
          <SelectField
            label="Roastery"
            value={roastery?._id ?? ""}
            onChange={(event) => setSelected(event.target.value)}
          >
            {mine.roasteries.map((row) => (
              <option key={row._id} value={row._id}>
                {row.name}
              </option>
            ))}
          </SelectField>
        )}
        <div className="flex flex-wrap gap-2">
          <SharedAction
            label="Create roastery"
            description="Start an empty shared workspace. Choose your internal QC method and, if applicable, its minimum passing score. These rules are recorded on each measured batch."
            fields={[
              { name: "name", label: "Roastery name" },
              {
                name: "method",
                label: "QC method",
                hint: "Use the name your team uses for its evaluation procedure.",
              },
              {
                name: "minimum",
                label: "Minimum score (optional, 0–100)",
                type: "number",
                min: 0,
                max: 100,
                optional: true,
              },
            ]}
            submit={async (data, operationKey) => {
              const id = await client.mutation(api.labs.roasteries.create, {
                name: text(data, "name"),
                qcMethod: text(data, "method"),
                minimumScore: text(data, "minimum")
                  ? number(data, "minimum")
                  : undefined,
                operationKey,
              });
              setSelected(id);
            }}
          />
        </div>
      </div>
      {!roastery ? (
        <Empty>
          Create a roastery to begin. Roasting is shared by admins and Russell.
        </Empty>
      ) : (
        <RoasteryWork
          key={roastery._id}
          roastery={roastery}
          accountId={mine.accountId}
          workspace={workspace}
          onOpen={onOpen}
        />
      )}
    </>
  );
}
function RoasteryWork({
  roastery,
  accountId,
  workspace,
  onOpen,
}: {
  roastery: Doc<"labsRoasteries"> & { role: string };
  accountId: Id<"userProfiles">;
  workspace: Workspace | null;
  onOpen: (workspace: Workspace) => void;
}) {
  const client = useConvex(),
    roasteryId = roastery._id;
  const [tab, setTab] = useState("day");
  const manager = ["owner", "manager"].includes(roastery.role),
    operator = manager || roastery.role === "operator",
    reviewer = manager || roastery.role === "qc";
  const machines = useQuery(api.labs.roasteries.machines, { roasteryId });
  const lots = usePaginatedQuery(
    api.labs.inventory.lots,
    { roasteryId },
    { initialNumItems: 25 },
  );
  const tasks = usePaginatedQuery(
    api.labs.inventory.tasks,
    { roasteryId },
    { initialNumItems: 25 },
  );
  const batches = usePaginatedQuery(
    api.labs.inventory.batches,
    { roasteryId },
    { initialNumItems: 25 },
  );
  const profiles = usePaginatedQuery(
    api.labs.recordings.profiles,
    { roasteryId },
    { initialNumItems: 25 },
  );
  const [review, setReview] = useState<Id<"labsBatches"> | null>(null);
  const taskFields: ActionField[] = [
    { name: "name", label: "Roast / work order name" },
    {
      name: "lot",
      label: "Physical green lot",
      options: lots.results.map((lot) => ({
        value: lot._id,
        label: `${lot.name} · ${lot.physicalLotCode} · ${fmt(lot.greenMg - lot.reservedMg)} available`,
      })),
    },
    {
      name: "machine",
      label: "Machine",
      options: (machines ?? []).map((row) => ({
        value: row._id,
        label: row.name,
      })),
    },
    {
      name: "profile",
      label: "Reference profile (optional, latest saved version)",
      optional: true,
      options: profiles.results
        .filter((profile) => profile.provenance !== "synthetic")
        .map((profile) => ({
          value: profile.latestVersionId,
          label: `${profile.name} · v${profile.revision}`,
        })),
    },
    { ...weight, label: "Green charge (kg)" },
  ];
  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b pb-4">
        <Badge variant="outline">{roastery.role}</Badge>
        {[
          ["day", "Roast day"],
          ["green", "Green stock"],
          ["qc", "Roasted stock & QC"],
          ["recordings", "Shared recordings"],
          ["ledger", "Movements"],
          ["settings", "Setup"],
        ].map(([id, label]) => (
          <Button
            key={id}
            size="sm"
            variant={tab === id ? "secondary" : "ghost"}
            aria-pressed={tab === id}
            onClick={() => setTab(id!)}
          >
            {label}
          </Button>
        ))}
      </div>
      {tab === "day" && (
        <Panel
          title="Roast day"
          description="Planning reserves green stock. Charge when coffee enters the drum."
          action={
            manager && (
              <SharedAction
                label="Plan roast"
                description="Reserve the measured green charge from one confirmed physical lot. No roasted stock is created until actual output is weighed."
                fields={taskFields}
                disabled={!machines?.length || !lots.results.length}
                submit={(data, operationKey) =>
                  client.mutation(api.labs.inventory.plan, {
                    roasteryId,
                    lotId: text(data, "lot") as Id<"labsGreenLots">,
                    machineId: text(data, "machine") as Id<"labsMachines">,
                    name: text(data, "name"),
                    chargeMg: kg(data, "weight"),
                    profileVersionId: text(data, "profile")
                      ? (text(data, "profile") as Id<"labsProfileVersions">)
                      : undefined,
                    operationKey,
                  })
                }
              />
            )
          }
        >
          {tasks.status === "LoadingFirstPage" ? (
            <p role="status">Loading roast tasks…</p>
          ) : tasks.results.length === 0 ? (
            <Empty>
              Receive a green lot and add a machine, then plan your first roast.
            </Empty>
          ) : (
            <div className="divide-y">
              {tasks.results.map((task) => (
                <div
                  key={task._id}
                  className="flex flex-wrap items-center justify-between gap-3 py-4"
                >
                  <div className="min-w-0 space-y-1">
                    <p className="font-medium">
                      {task.name}{" "}
                      <Badge variant="outline" className="ml-2">
                        {task.state}
                      </Badge>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {fmt(task.chargeMg)} green
                      {task.claimedBy === accountId
                        ? " · Assigned to you"
                        : task.claimedBy
                          ? " · Assigned to a teammate"
                          : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {operator && task.state === "planned" && (
                      <SharedAction
                        label="Claim task"
                        description={`Assign ${task.name} to you. Another operator will see that it is claimed.`}
                        submit={(_, operationKey) =>
                          client.mutation(api.labs.inventory.claim, {
                            roasteryId,
                            taskId: task._id,
                            operationKey,
                          })
                        }
                      />
                    )}
                    {task.claimedBy === accountId &&
                      task.state === "claimed" &&
                      operator && (
                        <SharedAction
                          label="Record charge"
                          description={`Confirm ${fmt(task.chargeMg)} of green coffee has entered the drum. This consumes reserved green stock. Start hardware capture separately at charge.`}
                          submit={(_, operationKey) =>
                            client.mutation(api.labs.inventory.charge, {
                              roasteryId,
                              taskId: task._id,
                              operationKey,
                            })
                          }
                        />
                      )}
                    {task.claimedBy === accountId &&
                      task.state === "charged" &&
                      operator && <MeasureTask task={task} />}
                    {manager && ["planned", "claimed"].includes(task.state) && (
                      <SharedAction
                        label="Cancel task"
                        description="Release this task's reservation. Use this only before coffee is charged."
                        fields={[reason]}
                        destructive
                        submit={(data, operationKey) =>
                          client.mutation(api.labs.inventory.cancel, {
                            roasteryId,
                            taskId: task._id,
                            reason: text(data, "reason"),
                            operationKey,
                          })
                        }
                      />
                    )}
                    {manager &&
                      task.claimedBy !== accountId &&
                      ["claimed", "charged"].includes(task.state) && (
                        <SharedAction
                          label="Take over"
                          description="Assign this active task to yourself and record why the operator changed."
                          fields={[reason]}
                          submit={(data, operationKey) =>
                            client.mutation(api.labs.inventory.takeOver, {
                              roasteryId,
                              taskId: task._id,
                              reason: text(data, "reason"),
                              operationKey,
                            })
                          }
                        />
                      )}
                    {manager && task.state === "charged" && (
                      <SharedAction
                        label="Record total loss"
                        description="Close this roast with no usable output. Its green coffee remains consumed."
                        fields={[reason]}
                        destructive
                        submit={(data, operationKey) =>
                          client.mutation(api.labs.inventory.discardTask, {
                            roasteryId,
                            taskId: task._id,
                            reason: text(data, "reason"),
                            operationKey,
                          })
                        }
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <More {...tasks} />
        </Panel>
      )}
      {tab === "green" && (
        <Panel
          title="Green stock"
          description="Each receipt identifies its physical lot and location."
          action={
            manager && (
              <SharedAction
                label="Receive green coffee"
                description="Record coffee physically received at this roastery. Enter the net weight and your receipt reference."
                fields={[
                  { name: "name", label: "Coffee name" },
                  { name: "code", label: "Physical lot code" },
                  { name: "supplier", label: "Supplier" },
                  { name: "location", label: "Stock location" },
                  weight,
                  reason,
                ]}
                submit={(data, operationKey) =>
                  client.mutation(api.labs.inventory.receive, {
                    roasteryId,
                    name: text(data, "name"),
                    physicalLotCode: text(data, "code"),
                    supplier: text(data, "supplier"),
                    location: text(data, "location"),
                    receivedMg: kg(data, "weight"),
                    reason: text(data, "reason"),
                    operationKey,
                  })
                }
              />
            )
          }
        >
          {lots.status === "LoadingFirstPage" ? (
            <p role="status">Loading green stock…</p>
          ) : !lots.results.length ? (
            <Empty>No green receipts yet.</Empty>
          ) : (
            <div className="divide-y">
              {lots.results.map((lot) => (
                <div
                  key={lot._id}
                  className="flex flex-wrap items-center justify-between gap-4 py-4"
                >
                  <div>
                    <p className="font-medium">{lot.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {lot.physicalLotCode} · {lot.supplier} · {lot.location}
                    </p>
                  </div>
                  <div className="text-sm tabular-nums">
                    <p>{fmt(lot.greenMg - lot.reservedMg)} available</p>
                    <p className="text-xs text-muted-foreground">
                      {fmt(lot.reservedMg)} reserved · {fmt(lot.greenMg)} on
                      hand
                    </p>
                  </div>
                  {manager && (
                    <SharedAction
                      label="Adjust stock"
                      description="Record a measured correction. Reserved coffee cannot be removed by an adjustment."
                      fields={[
                        {
                          name: "delta",
                          label: "Change in kg (negative to remove)",
                          type: "number",
                        },
                        reason,
                      ]}
                      submit={(data, operationKey) => {
                        const delta = number(data, "delta");
                        return client.mutation(api.labs.inventory.adjustGreen, {
                          roasteryId,
                          lotId: lot._id,
                          deltaMg:
                            Math.sign(delta) * toMassMg(Math.abs(delta), "kg"),
                          reason: text(data, "reason"),
                          operationKey,
                        });
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          <More {...lots} />
        </Panel>
      )}
      {tab === "qc" && (
        <>
          <Panel
            title="Roasted stock & QC"
            description={`Method: ${roastery.qcMethod}${roastery.minimumScore !== undefined ? ` · Minimum ${roastery.minimumScore}` : " · Explicit pass decision required"}`}
          >
            {batches.status === "LoadingFirstPage" ? (
              <p role="status">Loading batches…</p>
            ) : !batches.results.length ? (
              <Empty>
                Weigh a charged roast to create its batch and QC hold.
              </Empty>
            ) : (
              <div className="divide-y">
                {batches.results.map((batch) => (
                  <div
                    key={batch._id}
                    className="flex flex-wrap items-center justify-between gap-4 py-4"
                  >
                    <div>
                      <p className="font-medium">
                        {batch.name}{" "}
                        <Badge variant="outline" className="ml-2">
                          {batch.qcStatus === "hold"
                            ? "QC hold"
                            : batch.qcStatus}
                        </Badge>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {fmt(batch.remainingMg)} remaining ·{" "}
                        {((1 - batch.outputMg / batch.chargeMg) * 100).toFixed(
                          1,
                        )}
                        % measured shrink
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setReview(batch._id)}
                      >
                        Review QC
                      </Button>
                      {manager && batch.qcStatus === "released" && (
                        <SharedAction
                          label="Use roasted stock"
                          description="Deduct coffee used for packaging or another production step. Record the destination or work order in the reference."
                          fields={[weight, reason]}
                          submit={(data, operationKey) =>
                            client.mutation(api.labs.inventory.withdraw, {
                              roasteryId,
                              batchId: batch._id,
                              massMg: kg(data, "weight"),
                              reason: text(data, "reason"),
                              operationKey,
                            })
                          }
                        />
                      )}
                      {manager && batch.remainingMg > 0 && (
                        <SharedAction
                          label="Record waste"
                          description="Deduct measured roast waste, including coffee rejected by QC. This cannot be undone by editing a balance."
                          fields={[weight, reason]}
                          destructive
                          submit={(data, operationKey) =>
                            client.mutation(api.labs.inventory.waste, {
                              roasteryId,
                              batchId: batch._id,
                              massMg: kg(data, "weight"),
                              reason: text(data, "reason"),
                              operationKey,
                            })
                          }
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <More {...batches} />
          </Panel>
          {review && (
            <QualityReview
              key={review}
              roasteryId={roasteryId}
              batchId={review}
              canPull={operator || reviewer}
              canEvaluate={reviewer}
              manager={manager}
              onClose={() => setReview(null)}
            />
          )}
        </>
      )}
      {tab === "recordings" && (
        <SharedRecordings
          roasteryId={roasteryId}
          manager={manager}
          operator={operator}
          workspace={workspace}
          onOpen={onOpen}
        />
      )}
      {tab === "ledger" && <MovementLedger roasteryId={roasteryId} />}
      {tab === "settings" && (
        <Panel
          title="Roastery setup"
          description="Machine limits govern batch planning."
        >
          {!machines ? (
            <p role="status">Loading machines…</p>
          ) : machines.length ? (
            <div className="divide-y">
              {machines.map((machine) => (
                <p className="py-3 text-sm" key={machine._id}>
                  <strong className="font-medium">{machine.name}</strong> ·{" "}
                  {fmt(machine.minBatchMg)}–{fmt(machine.maxBatchMg)}
                </p>
              ))}
            </div>
          ) : (
            <Empty>
              Add your machine's working batch range to begin planning.
            </Empty>
          )}
          {manager && (
            <SharedAction
              label="Add machine"
              description="Enter the measured working range for your roaster. This registration does not connect its hardware."
              fields={[
                { name: "name", label: "Machine name" },
                {
                  name: "min",
                  label: "Minimum charge (kg)",
                  type: "number",
                  min: 0.000001,
                },
                {
                  name: "max",
                  label: "Maximum charge (kg)",
                  type: "number",
                  min: 0.000001,
                },
              ]}
              submit={(data, operationKey) =>
                client.mutation(api.labs.roasteries.addMachine, {
                  roasteryId,
                  name: text(data, "name"),
                  minBatchMg: kg(data, "min"),
                  maxBatchMg: kg(data, "max"),
                  operationKey,
                })
              }
            />
          )}
          <p className="border-t pt-4 text-sm text-muted-foreground">Access follows the internal app: admins and Russell. Tasks, stock and QC records are shared.</p>
        </Panel>
      )}
    </>
  );
}
function MeasureTask({ task }: { task: Doc<"labsTasks"> }) {
  const client = useConvex();
  const recordings = usePaginatedQuery(
    api.labs.recordings.list,
    { roasteryId: task.roasteryId },
    { initialNumItems: 25 },
  );
  return (
    <SharedAction
      label="Weigh roast"
      description="Record actual net output after cooling and tare. This creates roasted stock on QC hold. Attach a completed recording when available."
      fields={[
        weight,
        { name: "note", label: "Scale / tare observation" },
        {
          name: "recording",
          label: "Completed recording (optional)",
          optional: true,
          options: recordings.results
            .filter(
              (row) =>
                row.state === "sealed" &&
                row.provenance !== "synthetic" &&
                row.machineId === task.machineId,
            )
            .map((row) => ({ value: row._id, label: row.name })),
        },
      ]}
      submit={(data, operationKey) =>
        client.mutation(api.labs.inventory.measure, {
          roasteryId: task.roasteryId,
          taskId: task._id,
          outputMg: kg(data, "weight"),
          measurementNote: text(data, "note"),
          recordingId: text(data, "recording")
            ? (text(data, "recording") as Id<"labsRecordings">)
            : undefined,
          operationKey,
        })
      }
    />
  );
}
function QualityReview({
  roasteryId,
  batchId,
  canPull,
  canEvaluate,
  manager,
  onClose,
}: {
  roasteryId: Id<"labsRoasteries">;
  batchId: Id<"labsBatches">;
  canPull: boolean;
  canEvaluate: boolean;
  manager: boolean;
  onClose: () => void;
}) {
  const client = useConvex(),
    review = useQuery(api.labs.quality.batchReview, { roasteryId, batchId });
  if (!review) return <p role="status">Loading QC record…</p>;
  const { batch, samples, evaluations, currentEvaluations } = review;
  const passing =
    samples.length > 0 &&
    samples.every(
      (sample) =>
        currentEvaluations.find((row) => row?._id === sample.evaluationId)
          ?.decision === "pass",
    );
  return (
    <Panel
      title={`QC · ${batch.name}`}
      description={`${batch.qcMethod}${batch.minimumScore !== undefined ? ` · Pass requires ${batch.minimumScore} or above` : ""}`}
      action={
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close QC
        </Button>
      }
    >
      {canPull && batch.qcStatus === "hold" && (
        <SharedAction
          label="Pull sample"
          description="Deduct the physical sample weight once. Scoring or correcting its evaluation does not deduct it again."
          fields={[
            { name: "name", label: "Sample label" },
            {
              name: "grams",
              label: "Sample weight (g)",
              type: "number",
              min: 0.001,
            },
          ]}
          submit={(data, operationKey) =>
            client.mutation(api.labs.quality.pull, {
              roasteryId,
              batchId,
              name: text(data, "name"),
              massMg: toMassMg(number(data, "grams"), "g"),
              operationKey,
            })
          }
        />
      )}
      {!samples.length ? (
        <Empty>
          No sample pulled. Pull and evaluate a sample before release.
        </Empty>
      ) : (
        <div className="divide-y">
          {samples.map((sample) => {
            const evaluation = currentEvaluations.find(
              (row) => row?._id === sample.evaluationId,
            );
            return (
              <div
                key={sample._id}
                className="flex flex-wrap items-center justify-between gap-4 py-4"
              >
                <div>
                  <p className="font-medium">
                    {sample.name} · {sample.massMg / 1000} g
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {evaluation
                      ? `${evaluation.decision}${evaluation.score !== undefined ? ` · ${evaluation.score}` : ""} · ${evaluation.notes}`
                      : "Awaiting evaluation"}
                  </p>
                </div>
                {canEvaluate && batch.qcStatus === "hold" && (
                  <SharedAction
                    label={
                      evaluation ? "Correct evaluation" : "Evaluate sample"
                    }
                    description={
                      evaluation
                        ? "Append a corrected result with a reason. The previous evaluation stays in the history."
                        : "Record the cupping result using this batch's QC method. A pass still requires an explicit batch release."
                    }
                    fields={[
                      {
                        name: "score",
                        label: "Score (0–100)",
                        type: "number",
                        optional: batch.minimumScore === undefined,
                        min: 0,
                        max: 100,
                      },
                      {
                        name: "decision",
                        label: "Decision",
                        options: ["pass", "hold", "reject"].map((value) => ({
                          value,
                          label: value,
                        })),
                      },
                      {
                        name: "notes",
                        label: evaluation
                          ? "Correction reason and findings"
                          : "Findings",
                      },
                    ]}
                    submit={(data, operationKey) =>
                      client.mutation(api.labs.quality.evaluate, {
                        roasteryId,
                        sampleId: sample._id,
                        supersedes: sample.evaluationId,
                        method: batch.qcMethod,
                        score: text(data, "score")
                          ? number(data, "score")
                          : undefined,
                        decision: text(data, "decision") as
                          | "pass"
                          | "hold"
                          | "reject",
                        notes: text(data, "notes"),
                        operationKey,
                      })
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      {canEvaluate && batch.qcStatus === "hold" && (
        <SharedAction
          label="Release batch"
          disabled={!passing}
          description={`Release the remaining ${fmt(batch.remainingMg)} for use. Every sample must have a current passing result.`}
          submit={(_, operationKey) =>
            client.mutation(api.labs.quality.release, {
              roasteryId,
              batchId,
              evaluationId: samples[0]!.evaluationId!,
              operationKey,
            })
          }
        />
      )}
      {manager && batch.qcStatus === "rejected" && (
        <SharedAction
          label="Reopen QC review"
          description="Return this rejected batch to hold for documented reassessment. The rejection stays in its evaluation history."
          fields={[reason]}
          submit={(data, operationKey) =>
            client.mutation(api.labs.quality.reopen, {
              roasteryId,
              batchId,
              reason: text(data, "reason"),
              operationKey,
            })
          }
        />
      )}
      {evaluations.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer">
            Evaluation history · latest {evaluations.length}
          </summary>
          <ol className="mt-3 divide-y">
            {evaluations.map((row) => (
              <li key={row._id} className="py-3">
                <p>
                  {row.decision} · {row.score ?? "No score"} ·{" "}
                  {new Date(row.evaluatedAt).toLocaleString()}
                </p>
                <p className="text-muted-foreground">{row.notes}</p>
              </li>
            ))}
          </ol>
        </details>
      )}
    </Panel>
  );
}
function MovementLedger({ roasteryId }: { roasteryId: Id<"labsRoasteries"> }) {
  const ledger = usePaginatedQuery(
    api.labs.inventory.ledger,
    { roasteryId },
    { initialNumItems: 25 },
  );
  return (
    <Panel
      title="Inventory movements"
      description="Every balance change retains its reason and before/after weight."
    >
      {ledger.status === "LoadingFirstPage" ? (
        <p role="status">Loading movements…</p>
      ) : !ledger.results.length ? (
        <Empty>No inventory movements recorded yet.</Empty>
      ) : (
        <ol className="divide-y">
          {ledger.results.map((row) => (
            <li
              key={row._id}
              className="grid gap-1 py-4 text-sm sm:grid-cols-[1fr_auto]"
            >
              <p className="font-medium">
                {row.kind.replaceAll("_", " ")} · {row.account}
              </p>
              <p className="tabular-nums">
                {row.deltaMg > 0 ? "+" : "−"}
                {fmt(Math.abs(row.deltaMg))}
              </p>
              <p className="text-muted-foreground">{row.reason}</p>
              <p className="text-xs text-muted-foreground">
                {fmt(row.beforeMg)} → {fmt(row.afterMg)} ·{" "}
                {new Date(row.createdAt).toLocaleString()}
              </p>
            </li>
          ))}
        </ol>
      )}
      <More {...ledger} />
    </Panel>
  );
}
